"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  ThemeIcon,
  List,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconEye,
  IconSparkles,
  IconX,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { colorSeasons } from "@/constants";

export default function CoolWinterVsDeepWinterContent() {
  const t = useTranslations("coolWinterVsDeepWinter");

  const coolWinterData = colorSeasons.WINTER.subseasons.COOL;
  const deepWinterData = colorSeasons.WINTER.subseasons.DEEP;

  const comparisonPoints = [
    {
      aspect: "Color Depth",
      coolWinter: "Icy, clear colors with medium depth - think frozen lake",
      deepWinter: "Rich, deep colors with maximum depth - think midnight sky",
      icon: IconPalette,
    },
    {
      aspect: "Contrast Level",
      coolWinter: "High contrast with icy clarity",
      deepWinter: "Very high contrast with dramatic depth",
      icon: IconEye,
    },
    {
      aspect: "Best Colors",
      coolWinter: "Icy pink, bright white, cool berry, clear navy",
      deepWinter: "Burgundy, black, deep purple, forest green",
      icon: IconSparkles,
    },
    {
      aspect: "Avoid",
      coolWinter: "Warm tones, overly deep/heavy colors",
      deepWinter: "Pastels, light colors, anything washed out",
      icon: IconX,
    },
  ];

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            Cool Winter vs Deep Winter
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Both are cool winter subtypes. Cool Winter is icy and clear, while Deep Winter is rich and dramatic.
            Learn which matches your natural coloring.
          </Text>
        </Box>

        {/* Quick Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md" ta="center">
            Quick Comparison
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{coolWinterData.emoji}</Text>
                <Title order={3}>Cool Winter</Title>
                <Text size="sm" c="dimmed">
                  Icy and clear. Think frozen landscapes, cool jewel tones, crisp winter mornings.
                </Text>
                <Badge color="blue" variant="light" size="lg">
                  Cool + Icy + Clear
                </Badge>
                <Group gap="xs" justify="center">
                  {coolWinterData.primaryColors.slice(0, 6).map((color, i) => (
                    <Box key={i} w={28} h={28} style={{ background: color.hex, borderRadius: 4 }} />
                  ))}
                </Group>
              </Stack>
            </Paper>
            <Paper withBorder p="lg" radius="md">
              <Stack gap="md" ta="center">
                <Text size="xl">{deepWinterData.emoji}</Text>
                <Title order={3}>Deep Winter</Title>
                <Text size="sm" c="dimmed">
                  Rich and dramatic. Think midnight, deep jewels, luxurious velvets.
                </Text>
                <Badge color="violet" variant="light" size="lg">
                  Cool + Deep + Dramatic
                </Badge>
                <Group gap="xs" justify="center">
                  {deepWinterData.primaryColors.slice(0, 6).map((color, i) => (
                    <Box key={i} w={28} h={28} style={{ background: color.hex, borderRadius: 4 }} />
                  ))}
                </Group>
              </Stack>
            </Paper>
          </SimpleGrid>
        </Card>

        {/* Key Differences */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Key Differences
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {comparisonPoints.map((point, index) => (
              <Paper key={index} withBorder p="lg" radius="md">
                <Stack gap="md">
                  <Group>
                    <ThemeIcon color="blue" variant="light" size="lg">
                      <point.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {point.aspect}
                    </Text>
                  </Group>
                  <Box>
                    <Text fw={500} c="blue" size="sm" mb="xs">
                      Cool Winter:
                    </Text>
                    <Text size="sm" mb="md">
                      {point.coolWinter}
                    </Text>
                    <Text fw={500} c="violet" size="sm" mb="xs">
                      Deep Winter:
                    </Text>
                    <Text size="sm">{point.deepWinter}</Text>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </SimpleGrid>
        </Card>

        {/* Color Palette Comparison */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            Color Palette Comparison
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} mb="md" ta="center">Cool Winter Colors</Title>
              <Text size="sm" c="dimmed" ta="center" mb="md">Icy, clear, bright</Text>
              <SimpleGrid cols={4} spacing="xs">
                {coolWinterData.primaryColors.slice(0, 8).map((color, index) => (
                  <Box key={index}>
                    <Box
                      style={{
                        backgroundColor: color.hex,
                        height: 40,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Text size="xs" ta="center" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            <Box>
              <Title order={3} mb="md" ta="center">Deep Winter Colors</Title>
              <Text size="sm" c="dimmed" ta="center" mb="md">Rich, deep, dramatic</Text>
              <SimpleGrid cols={4} spacing="xs">
                {deepWinterData.primaryColors.slice(0, 8).map((color, index) => (
                  <Box key={index}>
                    <Box
                      style={{
                        backgroundColor: color.hex,
                        height: 40,
                        borderRadius: 4,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Text size="xs" ta="center" c="dimmed" mt={4}>{color.hex}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          </SimpleGrid>
        </Card>

        {/* How to Tell at Home */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="lg" ta="center">
            How to Tell the Difference at Home
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h4" mb="md">The Black Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Wear pure black and notice how it feels:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Cool Winter:</strong> Black works but icy white may feel more "you"</List.Item>
                <List.Item><strong>Deep Winter:</strong> Black feels powerful and completely natural</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md">The Depth Test</Title>
              <Text size="sm" c="dimmed" mb="sm">
                Compare a bright icy blue vs a deep navy:
              </Text>
              <List size="sm" spacing="xs">
                <List.Item><strong>Cool Winter:</strong> The icy blue brightens your face</List.Item>
                <List.Item><strong>Deep Winter:</strong> The deep navy brings out your drama</List.Item>
              </List>
            </Box>
          </SimpleGrid>
          <Paper withBorder p="md" radius="md" mt="lg" bg="gray.0">
            <Text size="sm" c="dimmed">
              Still unsure? These tests can be tricky in artificial lighting or with makeup on. A photo analysis removes the guesswork.
            </Text>
          </Paper>
        </Card>

        {/* Final CTA */}
        <Card withBorder p="xl" radius="md" bg="violet.0">
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2">
              Want a Definitive Answer?
            </Title>
            <Text size="lg" c="dimmed" maw={500} mx="auto">
              Upload a photo and get a confident result — no more guessing between Cool Winter and Deep Winter.
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/ai-color-analysis"
                color="violet"
                rightSection={<IconArrowRight size={20} />}
              >
                Confirm My Season
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-analysis-quiz"
                color="violet"
              >
                Start With Free Quiz
              </Button>
            </Flex>
            <Group justify="center" gap="xl" mt="md">
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/winter/cool"
                size="sm"
                color="gray"
              >
                View Cool Winter Palette →
              </Button>
              <Button
                variant="subtle"
                component={Link}
                href="/color-palette/winter/deep"
                size="sm"
                color="gray"
              >
                View Deep Winter Palette →
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
